import { useEffect, useState, useCallback } from 'react'
import { apiGet } from '../api'
import CostChart from './charts/CostChart'

interface CostResponse {
  fetched_at: number
  subtotal_cents: number
  vat_cents: number
  total_cents: number
  line_count: number
  top_movers: { label: string; cost_cents: number; hours: number; status: string }[]
  has_token: boolean
}
interface AppRow {
  id: string
  name: string
  app_group: string
  last_status: string | null
  civo_resource_id: string | null
  civo_status?: string | null
}
interface AppsResponse { apps: AppRow[]; fetched_at: number }
interface SessionRow { id: string; last_activity: number }
interface SessionsResponse { sessions: SessionRow[] }
interface IssuesSummary { p0: number; p1: number; p2: number; p3: number; blocked: number; total: number }
interface IssuesResponse { issues: unknown[]; summary: IssuesSummary }
interface TaskRow { id: string }
interface TasksResponse { tasks: TaskRow[] }
interface ForecastResponse {
  current_total_cents: number
  projected_eom_cents: number
  burn_per_hour_cents: number
  budget_cents?: number
  over_budget: boolean
  pct_of_budget?: number
}
interface ProvidersResponse {
  providers: Array<{ name: string; cents: number; note: string }>
  total_cents: number
}

function fmtCents(c: number): string {
  return `$${(c / 100).toFixed(2)}`
}

export default function HeroStrip() {
  const [cost, setCost] = useState<CostResponse | null>(null)
  const [apps, setApps] = useState<AppsResponse | null>(null)
  const [sessions, setSessions] = useState<SessionsResponse | null>(null)
  const [issues, setIssues] = useState<IssuesResponse | null>(null)
  const [tasks, setTasks] = useState<TasksResponse | null>(null)
  const [forecast, setForecast] = useState<ForecastResponse | null>(null)
  const [providers, setProviders] = useState<ProvidersResponse | null>(null)

  const load = useCallback(async () => {
    try {
      const [c, a, s, i, t, f, p] = await Promise.all([
        apiGet<CostResponse>('/infra/cost'),
        apiGet<AppsResponse>('/infra/apps'),
        apiGet<SessionsResponse>('/sessions'),
        apiGet<IssuesResponse>('/issues').catch(() => null),
        apiGet<TasksResponse>('/tasks?status=open').catch(() => null),
        apiGet<ForecastResponse>('/infra/cost/forecast').catch(() => null),
        apiGet<ProvidersResponse>('/infra/cost/providers').catch(() => null),
      ])
      setCost(c); setApps(a); setSessions(s); setIssues(i); setTasks(t); setForecast(f); setProviders(p)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => {
    load()
    const t = setInterval(load, 60_000)
    return () => clearInterval(t)
  }, [load])

  const upCount   = apps?.apps.filter(a => a.last_status === 'up').length ?? 0
  const downCount = apps?.apps.filter(a => a.last_status === 'down').length ?? 0
  const totalApps = apps?.apps.length ?? 0
  const liveVms   = apps?.apps.filter(a => a.civo_status === 'ACTIVE').length ?? 0
  const freshSess = sessions?.sessions.filter(s => Date.now() - s.last_activity < 24 * 3600_000).length ?? 0
  const issuesP0   = issues?.summary.p0 ?? 0
  const issuesP1   = issues?.summary.p1 ?? 0
  const issuesTotal= issues?.summary.total ?? 0
  const tasksOpen  = tasks?.tasks.length ?? 0
  // High-importance tasks are inbox-extracted urgent items + any imperatively flagged
  const urgentCount = tasks?.tasks.filter(t => (t as unknown as { importance?: string }).importance === 'high').length ?? 0

  return (
    <div className="hero-strip">
      <KpiCard label="Total spend · this month"
               value={providers ? fmtCents(providers.total_cents) : (cost ? fmtCents(cost.total_cents) : '—')}
               sub={
                 providers
                   ? providers.providers.filter(p => p.cents > 0)
                       .map(p => `${p.name} ${fmtCents(p.cents)}`).join(' · ')
                   : (forecast ? `→ ${fmtCents(forecast.projected_eom_cents)} eom` : '')
               }
               accent={forecast?.over_budget ? 'red' : 'cyan'}>
        <CostChart hours={720} compact height={42} />
      </KpiCard>

      <KpiCard label="Apps online"
               value={`${upCount} / ${totalApps}`}
               sub={downCount > 0 ? `${downCount} down` : 'all green'}
               accent={downCount > 0 ? 'red' : 'green'} />

      <KpiCard label="Live VMs / pools"
               value={`${liveVms}`}
               sub="ACTIVE on Civo right now"
               accent="warm" />

      <KpiCard label="Active sessions · 24h"
               value={`${freshSess}`}
               sub={sessions ? `${sessions.sessions.length} total` : ''}
               accent="claire" />

      <KpiCard label="Open issues"
               value={`${issuesTotal}`}
               sub={issuesP0 + issuesP1 > 0
                  ? `${issuesP0} P0 · ${issuesP1} P1`
                  : (issues ? 'no high-priority' : '—')}
               accent={issuesP0 > 0 ? 'red' : (issuesP1 > 0 ? 'warm' : 'cyan')} />

      <KpiCard label="Open todos"
               value={`${tasksOpen}`}
               sub={urgentCount > 0 ? `${urgentCount} urgent · ${tasksOpen - urgentCount} normal` : (tasksOpen > 0 ? 'across watched repos' : 'caught up')}
               accent={urgentCount > 0 ? 'red' : (tasksOpen > 10 ? 'warm' : (tasksOpen > 0 ? 'cyan' : 'green'))} />
    </div>
  )
}

function KpiCard({
  label, value, sub, accent, children,
}: {
  label: string
  value: string
  sub?: string
  accent: 'cyan' | 'warm' | 'claire' | 'green' | 'red'
  children?: React.ReactNode
}) {
  return (
    <div className={`kpi-card kpi-${accent}`}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
      {children && <div className="kpi-spark">{children}</div>}
    </div>
  )
}
